import { ContractLeftTarget, PaymentStatus } from '../player-auction.type';

export type BuyContractStatus = 'Active' | 'Done' | 'Canceled' | 'Expired' | 'Failed';
export type BuyAllocationStatus = 'Locked' | 'Bought' | 'Released' | 'Expired';
export type BuyTargetType = 'sent' | 'received';

/** Lifecycle of one (stock, buy contract) drain run. Pending/Active are non-terminal. */
export type StockLeaseStatus = 'Pending' | 'Active' | 'Drained' | 'Released' | 'Failed';

export const STOCK_LEASE_STATUS_OPTIONS: StockLeaseStatus[] = ['Pending', 'Active', 'Drained', 'Released', 'Failed'];

export const BUY_CONTRACT_STATUS_OPTIONS: BuyContractStatus[] = ['Active', 'Done', 'Canceled', 'Expired', 'Failed'];

export interface BuyContractAllocationsSummary {
  total: number;
  locked: number;
  bought: number;
  released: number;
  expired: number;
}

export interface BuyContract {
  id: string;
  name?: string;
  note?: string;
  buyer_id: string;
  target_coins: number;
  target_type: BuyTargetType;
  status: BuyContractStatus;
  fee: number;
  /** Computed: fee * (received_coins / 100_000) — read-only */
  fee_amount: number;
  payment_status: PaymentStatus;
  archived: boolean;
  sent_coins: number;
  received_coins: number;
  allocations?: BuyContractAllocationsSummary;
  started_at?: string;
  finished_at?: string;
  created_at?: string;
  updated_at?: string;
  failed_reason?: string;
}

/** One (stock, buy contract) drain run — GET .../admin/buy-contract/:id/leases */
export interface StockLease {
  id: string;
  buy_contract_id: string;
  stock_id: string;
  status: StockLeaseStatus;
  session_id?: string;
  start_coins: number;
  last_coins: number;
  /** The stock's per-account floor; the lease never spends below it. */
  coins_floor: number;
  cards_bought: number;
  /** Set while EA has rate-limited this stock: the lease is slower, not dead. */
  rest_until?: string;
  failed_reason?: string;
  started_at?: string;
  finished_at?: string;
  heartbeat_at?: string;
  created_at?: string;
}

export interface StockLeasesResult {
  items: StockLease[];
  total: number;
}

export interface BuyContractAllocation {
  id: string;
  card_id: string;
  player_id: number;
  player_name: string;
  player_nickname: string;
  player_image_path: string;
  buy_now_price: number;
  bid_price: number;
  quality: string;
  rarity_id: number;
  status: BuyAllocationStatus;
  lock_expires_at?: string;
  expire_time?: string;
}

export interface CreateBuyContractRequest {
  target_coins: number;
  target_type?: BuyTargetType;
  name?: string;
  note?: string;
  fee?: number;
}

export interface UpdateBuyContractRequest {
  name?: string | null;
  note?: string | null;
  target_coins?: number | null;
}

export type AutoBuySupplierScope = 'all' | 'selected';

/**
 * GET/PATCH /player-auction/admin/auto-buy — the singleton continuous auto-buy
 * controller. buy_contract_id is its hidden ledger: it is never listed among
 * buy contracts, but the admin lease endpoints still accept it.
 */
export interface AutoBuyConfig {
  configured: boolean;
  buy_contract_id?: string;
  is_active: boolean;
  desired_concurrent_stocks: number;
  supplier_scope: AutoBuySupplierScope;
  supplier_ids: string[];
  active_leases: number;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
}

/** Omitted fields keep their value; supplier_ids: [] clears the selection. */
export interface UpdateAutoBuyConfigRequest {
  is_active?: boolean;
  /** 1-100 */
  desired_concurrent_stocks?: number;
  supplier_scope?: AutoBuySupplierScope;
  supplier_ids?: string[];
}

export interface GetBuyContractsRequest {
  pageIndex?: number;
  pageSize?: number;
  status?: BuyContractStatus;
  search?: string;
  archived?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface ListBuyContractsResult {
  items: BuyContract[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}

export interface BuyContractAllocationsResult {
  items: BuyContractAllocation[];
  total: number;
}

/** GET /player-auction/public/buy-contract/:id — no auth, no buyer identity exposed */
export interface PublicBuyContractResponse {
  id: string;
  name?: string;
  note?: string;
  target_coins: number;
  target_type: BuyTargetType;
  status: BuyContractStatus;
  fee: number;
  fee_amount: number;
  payment_status: PaymentStatus;
  sent_coins: number;
  received_coins: number;
  allocations: BuyContractAllocationsSummary | null;
  started_at?: string;
  finished_at?: string;
  created_at: string;
  updated_at: string;
  failed_reason: string | null;
}

// Admin billing
export interface AdminBuyContractResponse extends BuyContract {
  buyer_username: string;
  buyer_roles: string[];
}

/** Row shape usable for both the regular and admin buy-contract lists. */
export type BuyContractRow = BuyContract & Partial<Pick<AdminBuyContractResponse, 'buyer_username' | 'buyer_roles'>>;

export interface GetAdminBuyContractsRequest {
  pageIndex?: number;
  pageSize?: number;
  status?: BuyContractStatus;
  search?: string;
  ownerRole?: string;
  archived?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface GetAdminBuyContractsResult {
  items: AdminBuyContractResponse[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}

export interface GetBuyContractStatsRequest {
  search?: string;
  archived?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface ArchiveInactiveBuyContractsRequest {
  search?: string;
  /** Admin scope only. */
  ownerRole?: string;
  date_from?: string;
  date_to?: string;
}

export interface ArchiveInactiveBuyContractsResult {
  archived: number;
}

/** The sell contract that listed a card, plus the card's own details. */
export interface CardListing {
  card_id: string;
  trade_id_str: string;
  player_id: number;
  player_name: string;
  player_nickname: string;
  player_image_path: string;
  buy_now_price: number;
  bid_price: number;
  quality: string;
  rarity_id: number;
  card_status: string;
  sell_contract_id: string;
  sell_contract_name?: string;
  sell_contract_status: string;
  sell_contract_started_at?: string;
  sell_contract_expire_at?: string;
  sell_contract_finished_at?: string;
}

/** One buy-contract allocation a card has ever had. */
export interface AllocationHistoryEntry {
  id: string;
  buy_contract_id: string;
  buy_contract_name?: string;
  buy_contract_status: string;
  status: BuyAllocationStatus;
  locked_at?: string;
  lock_expires_at?: string;
  bought_at?: string;
  created_at?: string;
}

/** GET /player-auction/admin/buy-contract/card/:cardId/history — admin only */
export interface CardHistory {
  listing: CardListing;
  allocations: AllocationHistoryEntry[];
}

export interface BuyContractStatsReport {
  total_contracts: number;
  status_counts: Partial<Record<BuyContractStatus, number>>;
  total_target: number;
  total_sent: number;
  total_received: number;
  total_fee: number;
  avg_fee: number;
  /** Remaining coins to hit target, summed only over open (Active) contracts. */
  left_target: ContractLeftTarget;
}

/** A supplier's refusal of one (player, bid, buy-now) tuple. */
export interface TupleReport {
  id: string;
  reporter_id: string;
  reporter_username: string;
  buy_contract_id: string;
  player_id: number;
  player_name?: string;
  bid_price: number;
  buy_now_price: number;
  released_cards: number;
  /** Distinct suppliers reporting this tuple right now; at the threshold only auto-buy gets it. */
  live_reporters: number;
  /** False once the reported listing has expired. */
  live: boolean;
  expires_at: string;
  created_at: string;
}

export interface ListTupleReportsResponse {
  items: TupleReport[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}
